/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
// FLAGGED-FOR-REVIEW-2026-02: This file has residual unused vars / state
// setters / effect-deps that look like DROPPED-FEATURE references, not
// blind-orphan code (state setters never called, computed values never
// rendered, hooks with intentional dep omissions). Reviewer next step:
// walk each warning and decide either RESTORE the callsite that references
// the var, OR delete the var. Do NOT bulk-delete without review — several
// of these signal features that were half-migrated.
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  Users, Film, BarChart3, DollarSign, GripVertical, Calendar, MapPin,
  Save, Upload, Loader2, Navigation, Clock, DollarSign as DollarIcon, Receipt
} from 'lucide-react';
import { Card } from '../components/ui/card';
// eslint-disable-next-line no-unused-vars -- FLAGGED (2026-02): Badge was
// wired for department pills in Production page but the render block was
// removed. Restore <Badge> in dept-row OR delete this import.
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { ImageLightbox } from '../components/ImageLightbox';
import { AuthImage } from '../components/AuthImage';
import BudgetPage from './BudgetPage';
import { toast } from 'sonner';
import { MLText } from '../components/MLText';
import { getBreakdownItemLabel } from '../utils/breakdownLabel';
import { formatSceneNumber } from '../utils/sceneNumber';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductionPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Locations tab state
  const [locationsData, setLocationsData] = useState([]); // unique locations from script
  const [locSpecs, setLocSpecs] = useState([]);
  const [locSpecsEdits, setLocSpecsEdits] = useState({});
  const [locSaving, setLocSaving] = useState({});
  const [locGallery, setLocGallery] = useState([]);
  const [locGalleryLoading, setLocGalleryLoading] = useState(false);
  const [locPhotoUploading, setLocPhotoUploading] = useState({});
  const [locLightbox, setLocLightbox] = useState(null);
  const fileInputRefs = useRef({});

  const headers = { Authorization: `Bearer ${token}` };

  const galleryByLocation = useMemo(() => {
    const map = {};
    locGallery.forEach(ref => {
      const loc = (ref.location_name || 'Unknown').trim();
      if (!map[loc]) map[loc] = [];
      map[loc].push(ref);
    });
    return map;
  }, [locGallery]);

  useEffect(() => { fetchStats(); }, [projectId]);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const [breakdownRes, budgetRes] = await Promise.all([
        axios.get(`${API}/projects/${projectId}/breakdown`, { headers }),
        axios.get(`${API}/projects/${projectId}/budget`, { headers })
      ]);
      const sceneData = breakdownRes.data;
      const totalItems = sceneData.reduce((s, sc) => s + Object.values(sc.breakdown_items || {}).reduce((ss, arr) => ss + arr.length, 0), 0);
      const characters = new Set();
      sceneData.forEach(s => (s.characters_in_scene || []).forEach(c => characters.add(c)));
      const totalPages = sceneData.reduce((s, sc) => s + (sc.page_count_eighths || 0), 0);
      setStats({
        sceneCount: sceneData.length, characterCount: characters.size,
        totalItems, totalPages,
        budgetTotal: budgetRes.data.grand_total, budgetLines: budgetRes.data.lines?.length || 0,
        scenes: sceneData
      });
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  const fetchLocations = async () => {
    try {
      const [locsRes, specsRes] = await Promise.all([
        axios.get(`${API}/projects/${projectId}/locations`, { headers }),
        axios.get(`${API}/projects/${projectId}/location-specs`, { headers })
      ]);
      setLocationsData(locsRes.data || []);
      const specs = specsRes.data || [];
      setLocSpecs(specs);
      const editsMap = {};
      specs.forEach(s => { editsMap[s.location_name] = { ...s }; });
      setLocSpecsEdits(editsMap);
    } catch { toast.error('Failed to load locations'); }
  };

  const fetchLocGallery = async () => {
    setLocGalleryLoading(true);
    try {
      const r = await axios.get(`${API}/projects/${projectId}/gallery?department=ART`, { headers });
      const flat = [];
      (r.data || []).forEach(group => {
        (group.rooms || []).forEach(room => {
          (room.items || []).forEach(item => flat.push({ ...item, location_name: group.location_name }));
        });
      });
      setLocGallery(flat);
    } catch { /* silent */ }
    setLocGalleryLoading(false);
  };

  const handleSaveLocationSpec = async (locName) => {
    setLocSaving(p => ({ ...p, [locName]: true }));
    try {
      const editedSpec = locSpecsEdits[locName] || {};
      const newSpecs = locSpecs.filter(s => s.location_name !== locName);
      newSpecs.push({ ...editedSpec, location_name: locName });
      await axios.put(`${API}/projects/${projectId}/location-specs`, newSpecs, { headers });
      setLocSpecs(newSpecs);
      toast.success(`Saved specs for ${locName}`);
    } catch { toast.error('Failed to save'); }
    setLocSaving(p => ({ ...p, [locName]: false }));
  };

  const handleLocPhotoUpload = async (locName, file) => {
    if (!file) return;
    setLocPhotoUploading(p => ({ ...p, [locName]: true }));
    const form = new FormData();
    form.append('file', file);
    try {
      await axios.post(
        `${API}/projects/${projectId}/gallery?department=ART&location_name=${encodeURIComponent(locName)}&room_name=Reference`,
        form, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } }
      );
      toast.success('Photo uploaded');
      fetchLocGallery();
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    setLocPhotoUploading(p => ({ ...p, [locName]: false }));
  };

  return (
    <div className="min-h-screen bg-[#0d0d0d] text-[#f0f0f0]">
      <div className="border-b border-[#2a2a2a] px-6 py-5">
        <div>
          <h1 className="font-['DM_Serif_Display'] text-2xl text-[#f0f0f0]">Production</h1>
          <p className="text-sm text-[#a0a0a0] mt-0.5">Overview, budget management, locations, and production notes.</p>
        </div>
        <div className="flex gap-1 mt-4 bg-[#1a1a1a] border border-[#2a2a2a] rounded-lg p-1 w-fit">
          {[
            ['overview', 'Overview'],
            ['budget', 'Budget'],
            ['locations', 'Locations'],
            ['notes', 'Production Notes']
          ].map(([key, label]) => (
            <button
              key={key}
              onClick={() => {
                setActiveTab(key);
                if (key === 'locations') { fetchLocations(); fetchLocGallery(); }
              }}
              className={`px-4 py-1.5 rounded-md text-sm transition-colors ${activeTab === key ? 'bg-[#e8c547] text-black font-medium' : 'text-[#a0a0a0] hover:text-[#f0f0f0]'}`}
              data-testid={`prod-tab-${key}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* OVERVIEW TAB */}
      {activeTab === 'overview' && (
        <div className="p-6">
          {loading ? <div className="text-center py-12 text-[#a0a0a0]">Loading...</div> : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                {[
                  { label: 'Total Scenes', value: stats?.sceneCount, icon: Film, color: 'text-blue-400' },
                  { label: 'Characters', value: stats?.characterCount, icon: Users, color: 'text-purple-400' },
                  { label: 'Total Pages', value: `${Math.floor((stats?.totalPages || 0) / 8)} ${(stats?.totalPages || 0) % 8}/8`, icon: BarChart3, color: 'text-amber-400' },
                  { label: 'Budget Total', value: `$${(stats?.budgetTotal || 0).toLocaleString()}`, icon: DollarSign, color: 'text-green-400' },
                ].map(stat => (
                  <Card key={stat.label} className="bg-[#1a1a1a] border-[#2a2a2a] p-5" data-testid={`stat-${stat.label.toLowerCase().replace(' ', '-')}`}>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-xs text-[#a0a0a0] font-medium">{stat.label}</span>
                      <stat.icon className={`h-4 w-4 ${stat.color}`} />
                    </div>
                    <div className="text-2xl font-bold text-[#f0f0f0]">{stat.value}</div>
                  </Card>
                ))}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card className="bg-[#1a1a1a] border-[#2a2a2a] p-5">
                  <div className="font-semibold text-[#f0f0f0] mb-4">Quick Access</div>
                  <div className="space-y-2">
                    <Button variant="outline" className="w-full justify-start border-[#2a2a2a] text-[#f0f0f0] hover:bg-[#0d0d0d]"
                      onClick={() => setActiveTab('budget')} data-testid="goto-budget-btn">
                      <DollarSign className="h-4 w-4 mr-2 text-green-400" /> Budget — {stats?.budgetLines} lines · ${(stats?.budgetTotal || 0).toLocaleString()}
                    </Button>
                    <Button variant="outline" className="w-full justify-start border-[#2a2a2a] text-[#f0f0f0] hover:bg-[#0d0d0d]"
                      onClick={() => navigate(`/projects/${projectId}/stripboard`)} data-testid="goto-stripboard-btn">
                      <GripVertical className="h-4 w-4 mr-2 text-[#e8c547]" /> Strip Board Schedule
                    </Button>
                  </div>
                </Card>
                <Card className="bg-[#1a1a1a] border-[#2a2a2a] p-5">
                  <div className="font-semibold text-[#f0f0f0] mb-4">Scenes by Type</div>
                  <div className="space-y-2">
                    {['INT', 'EXT'].map(type => {
                      const count = stats?.scenes?.filter(s => s.location_type === type).length || 0;
                      return (
                        <div key={type} className="flex items-center gap-3">
                          <span className="text-xs font-semibold text-[#a0a0a0] w-8">{type}</span>
                          <div className="flex-1 bg-[#2a2a2a] rounded-full h-1.5">
                            <div className="bg-[#e8c547] h-1.5 rounded-full" style={{ width: `${stats?.sceneCount ? (count / stats.sceneCount) * 100 : 0}%` }} />
                          </div>
                          <span className="text-xs text-[#a0a0a0] w-6">{count}</span>
                        </div>
                      );
                    })}
                    {['DAY', 'NIGHT', 'DAWN', 'DUSK'].map(tod => {
                      const count = stats?.scenes?.filter(s => s.time_of_day === tod).length || 0;
                      if (!count) return null;
                      return (
                        <div key={tod} className="flex items-center gap-3">
                          <span className="text-xs font-semibold text-[#a0a0a0] w-8">{tod}</span>
                          <div className="flex-1 bg-[#2a2a2a] rounded-full h-1.5">
                            <div className="bg-amber-500/60 h-1.5 rounded-full" style={{ width: `${stats?.sceneCount ? (count / stats.sceneCount) * 100 : 0}%` }} />
                          </div>
                          <span className="text-xs text-[#a0a0a0] w-6">{count}</span>
                        </div>
                      );
                    })}
                  </div>
                </Card>
              </div>
            </>
          )}
        </div>
      )}

      {/* BUDGET TAB */}
      {activeTab === 'budget' && <BudgetPage embedded />}

      {/* LOCATIONS TAB */}
      {activeTab === 'locations' && (
        <div className="p-6 space-y-6">
          <div>
            <h2 className="font-['DM_Serif_Display'] text-xl text-[#f0f0f0]">Production Locations</h2>
            <p className="text-xs text-[#a0a0a0] mt-0.5">
              {locationsData.length} location{locationsData.length !== 1 ? 's' : ''} from your script. Set availability, rent costs, and manage reference photos.
            </p>
          </div>
          {locationsData.length === 0 ? (
            <Card className="bg-[#1a1a1a] border-[#2a2a2a] p-12 text-center">
              <MapPin className="h-12 w-12 mx-auto mb-3 text-[#2a2a2a]" />
              <p className="text-[#a0a0a0]">No locations found. Upload a script to populate locations.</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {locationsData.map(loc => {
                const locName = loc.location_name;
                const photos = galleryByLocation[locName] || [];
                const spec = locSpecsEdits[locName] || locSpecs.find(s => s.location_name === locName) || {};
                const isSav = locSaving[locName] || false;
                const isUpload = locPhotoUploading[locName] || false;

                return (
                  <div
                    key={locName}
                    className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl overflow-hidden"
                    data-testid={`prod-loc-card-${locName.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {/* Header */}
                    <div className="bg-[#0d0d0d] px-4 py-3 flex items-center justify-between border-b border-[#2a2a2a]">
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-[#e8c547]" />
                        <span className="font-semibold text-[#f0f0f0]">{locName}</span>
                        <span className="text-xs text-[#a0a0a0]">{loc.scene_ids.length} scene{loc.scene_ids.length !== 1 ? 's' : ''}</span>
                      </div>
                      <div className="flex gap-1">
                        {(loc.scene_numbers || []).slice(0, 5).map(n => (
                          <span key={n} className="text-[9px] bg-[#e8c547]/20 text-[#e8c547] px-1 rounded font-mono">#{n}</span>
                        ))}
                      </div>
                    </div>

                    {/* Photo strip */}
                    <div className="p-3 border-b border-[#2a2a2a]">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-[10px] font-semibold text-[#a0a0a0] uppercase tracking-widest">Reference Photos</span>
                        {locGalleryLoading && <Loader2 className="h-3 w-3 animate-spin text-[#a0a0a0]" />}
                      </div>
                      <div className="flex items-center gap-2 overflow-x-auto pb-1">
                        {photos.length === 0 && !locGalleryLoading && (
                          <span className="text-xs text-[#444] italic shrink-0">No photos yet</span>
                        )}
                        {photos.map((photo, pIdx) => (
                          <AuthImage
                            key={photo.id}
                            path={photo.storage_path}
                            alt={photo.original_filename}
                            className="h-20 w-28 object-cover rounded-lg shrink-0 border border-[#2a2a2a] hover:border-[#e8c547] transition-colors cursor-pointer hover:scale-105"
                            onClick={() => setLocLightbox({
                              images: photos.map(p => ({ path: p.storage_path, alt: p.original_filename })),
                              idx: pIdx
                            })}
                            data-testid={`prod-loc-photo-${photo.id}`}
                          />
                        ))}
                        <button
                          className="h-20 w-20 border-2 border-dashed border-[#2a2a2a] hover:border-[#e8c547] rounded-lg flex flex-col items-center justify-center gap-1 text-[#555] hover:text-[#e8c547] transition-colors shrink-0"
                          onClick={() => fileInputRefs.current[locName]?.click()}
                          disabled={isUpload}
                          data-testid={`prod-upload-loc-photo-${locName}`}
                        >
                          {isUpload ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                          <span className="text-[9px]">Add Photo</span>
                        </button>
                        <input
                          type="file" accept="image/*" className="hidden"
                          ref={el => { fileInputRefs.current[locName] = el; }}
                          onChange={e => { if (e.target.files?.[0]) handleLocPhotoUpload(locName, e.target.files[0]); e.target.value = ''; }}
                        />
                      </div>
                    </div>

                    {/* Spec fields */}
                    <div className="p-4 space-y-3">
                      <div className="text-[10px] font-semibold text-[#a0a0a0] uppercase tracking-widest">Specifications</div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="flex items-center gap-1 text-[10px] text-[#a0a0a0] mb-1">
                            <Calendar className="h-3 w-3" /> Available Days
                          </label>
                          <Input value={spec.available_days || ''} onChange={e => setLocSpecsEdits(p => ({ ...p, [locName]: { ...spec, available_days: e.target.value } }))}
                            placeholder="Mon,Tue,Wed" className="bg-[#0d0d0d] border-[#2a2a2a] text-[#f0f0f0] h-7 text-xs" data-testid={`prod-loc-avail-${locName}`} />
                        </div>
                        <div>
                          <label className="flex items-center gap-1 text-[10px] text-[#a0a0a0] mb-1">
                            <Clock className="h-3 w-3" /> Shooting Hours
                          </label>
                          <Input value={spec.available_hours || ''} onChange={e => setLocSpecsEdits(p => ({ ...p, [locName]: { ...spec, available_hours: e.target.value } }))}
                            placeholder="06:00-20:00" className="bg-[#0d0d0d] border-[#2a2a2a] text-[#f0f0f0] h-7 text-xs" data-testid={`prod-loc-hours-${locName}`} />
                        </div>
                        <div>
                          <label className="flex items-center gap-1 text-[10px] text-[#a0a0a0] mb-1">
                            <MapPin className="h-3 w-3" /> Distance (km)
                          </label>
                          <Input type="number" value={spec.distance_km || ''} onChange={e => setLocSpecsEdits(p => ({ ...p, [locName]: { ...spec, distance_km: parseFloat(e.target.value) || null } }))}
                            placeholder="e.g. 12" className="bg-[#0d0d0d] border-[#2a2a2a] text-[#f0f0f0] h-7 text-xs" data-testid={`prod-loc-km-${locName}`} />
                        </div>
                        <div>
                          <label className="flex items-center gap-1 text-[10px] text-[#a0a0a0] mb-1">
                            <Navigation className="h-3 w-3" /> Direction from Base
                          </label>
                          <Input value={spec.direction || ''} onChange={e => setLocSpecsEdits(p => ({ ...p, [locName]: { ...spec, direction: e.target.value } }))}
                            placeholder="e.g. 10 kms via NH47" className="bg-[#0d0d0d] border-[#2a2a2a] text-[#f0f0f0] h-7 text-xs" data-testid={`prod-loc-direction-${locName}`} />
                        </div>
                        {/* Rent fields */}
                        <div>
                          <label className="flex items-center gap-1 text-[10px] text-[#a0a0a0] mb-1">
                            <DollarIcon className="h-3 w-3 text-[#e8c547]" /> Rent / Day
                          </label>
                          <Input type="number" value={spec.rent_per_day || ''} onChange={e => setLocSpecsEdits(p => ({ ...p, [locName]: { ...spec, rent_per_day: parseFloat(e.target.value) || null } }))}
                            placeholder="e.g. 5000" className="bg-[#0d0d0d] border-[#e8c547]/30 text-[#f0f0f0] h-7 text-xs" data-testid={`prod-loc-rent-${locName}`} />
                        </div>
                        <div>
                          <label className="flex items-center gap-1 text-[10px] text-[#a0a0a0] mb-1">
                            <Receipt className="h-3 w-3 text-[#4ade80]" /> Corrected Rent
                          </label>
                          <Input type="number" value={spec.corrected_rent || ''} onChange={e => setLocSpecsEdits(p => ({ ...p, [locName]: { ...spec, corrected_rent: parseFloat(e.target.value) || null } }))}
                            placeholder="Negotiated rent" className="bg-[#0d0d0d] border-[#4ade80]/30 text-[#f0f0f0] h-7 text-xs" data-testid={`prod-loc-corrected-rent-${locName}`} />
                        </div>
                      </div>
                      <div>
                        <label className="text-[10px] text-[#a0a0a0] mb-1 block">Notes</label>
                        <Input value={spec.notes || ''} onChange={e => setLocSpecsEdits(p => ({ ...p, [locName]: { ...spec, notes: e.target.value } }))}
                          placeholder="Permit required, noise restrictions, etc." className="bg-[#0d0d0d] border-[#2a2a2a] text-[#f0f0f0] h-7 text-xs" data-testid={`prod-loc-notes-${locName}`} />
                      </div>
                      {/* Rent summary */}
                      {(spec.rent_per_day || spec.corrected_rent) && (
                        <div className="flex gap-3 p-2 bg-[#0d0d0d] rounded-lg border border-[#2a2a2a]">
                          {spec.rent_per_day && (
                            <div className="text-xs">
                              <span className="text-[#a0a0a0]">Original: </span>
                              <span className="text-[#e8c547] font-semibold">₹{Number(spec.rent_per_day).toLocaleString()}/day</span>
                            </div>
                          )}
                          {spec.corrected_rent && (
                            <div className="text-xs">
                              <span className="text-[#a0a0a0]">Negotiated: </span>
                              <span className="text-[#4ade80] font-semibold">₹{Number(spec.corrected_rent).toLocaleString()}/day</span>
                            </div>
                          )}
                          {spec.rent_per_day && spec.corrected_rent && (
                            <div className="text-xs">
                              <span className="text-[#a0a0a0]">Saved: </span>
                              <span className={spec.corrected_rent < spec.rent_per_day ? 'text-[#4ade80] font-semibold' : 'text-red-400 font-semibold'}>
                                ₹{Math.abs(Number(spec.rent_per_day) - Number(spec.corrected_rent)).toLocaleString()}/day
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                      <div className="flex justify-end">
                        <Button size="sm" onClick={() => handleSaveLocationSpec(locName)} disabled={isSav}
                          className="bg-[#e8c547] hover:bg-[#d6b539] text-black h-7 text-xs" data-testid={`prod-save-loc-${locName}`}>
                          {isSav ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Save className="h-3 w-3 mr-1" />}
                          Save
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {locLightbox && (
            <ImageLightbox images={locLightbox.images} initialIndex={locLightbox.idx} onClose={() => setLocLightbox(null)} />
          )}
        </div>
      )}

      {/* PRODUCTION NOTES TAB */}
      {activeTab === 'notes' && (
        <div className="p-6">
          {loading ? <div className="text-center py-12 text-[#a0a0a0]">Loading...</div> : (
            <div className="space-y-4">
              {stats?.scenes?.map(scene => {
                const notes = scene.breakdown_items?.PRODUCTION_NOTE || [];
                if (!notes.length) return null;
                return (
                  <Card key={scene.id} className="bg-[#1a1a1a] border-[#2a2a2a] p-4" data-testid={`prod-note-${scene.scene_number ?? 'unnumbered'}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-xs font-bold text-[#e8c547] bg-[#2a2a2a] h-6 w-6 rounded flex items-center justify-center">{formatSceneNumber(scene.scene_number)}</span>
                      <span className="font-medium text-[#f0f0f0] text-sm">{scene.scene_header}</span>
                    </div>
                    <div className="space-y-2">
                      {notes.map(item => (
                        <div key={item.id} className="text-sm text-[#f0f0f0] bg-[#0d0d0d] rounded p-2 border-l-2 border-[#e8c547]">
                          <MLText as="span">{getBreakdownItemLabel(item.description, 'original')}</MLText>
                        </div>
                      ))}
                    </div>
                  </Card>
                );
              })}
              {!stats?.scenes?.some(s => (s.breakdown_items?.PRODUCTION_NOTE || []).length > 0) && (
                <Card className="bg-[#1a1a1a] border-[#2a2a2a] p-12 text-center">
                  <p className="text-[#a0a0a0]">No production notes yet</p>
                </Card>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
